/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-unresolved */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MoreVert } from '@mui/icons-material';
import { Avatar, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, Tooltip } from '@mui/material';
import { isEmpty, isNull, isUndefined, orderBy } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import type { CustomUser } from 'api';
import { companiesAction, fundsAction, globalAction } from 'common/actions';
import { firmCompGroupsUrl, firmSummaryUrl, userProfileTabUrl } from 'common/config/urls';
import { CREATE_FIRM_ENABLER } from 'common/constants/features';
import {
  ARROW_DOWN_KEY,
  ARROW_UP_KEY,
  ESCAPE_KEY,
  FILTER_FIRM_NUMBER,
  FIRMS_MENU_SLUG,
  ITEM_HEIGHT,
  SELECT_A_FIRM,
  SELECT_FIRM,
  USER_MENU_SLUG,
} from 'common/constants/sidebar';
import { ACCOUNT_SETTINGS_TAB, AUDIT_LOGS_TAB, VALID_ROLES_FOR_AUDIT_LOGS } from 'common/constants/user';
import { PINNED_FIRMS_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import { Firm as StoreFirm } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import FeatureEnabler from 'components/FeatureEnabler';
import FeaturesContext from 'context/FeaturesContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { WORKBOARD_CURRENT_VALUATIONS_URL } from 'dashboard409a/common/config/urls';
import { getPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import { PushPin } from 'icons';
import { useSignOutAction } from 'pages/Auth/hooks';
import { clearAppAutoSelect, savePreviousPortfolioRoute } from 'pages/Auth/utils';
import { ShowFeedbackWidget } from 'providers';
import { capitalizeWords, getInitials } from 'utilities';
import FirmFilter from '../FirmFilter';
import { BottomMenuItemsProps } from './BottomMenuItems.props';
import {
  OverflowText,
  StyledAvatar,
  StyledBottomMenuSubMenuIcon,
  StyledLink,
  StyledMenuItem,
  StyledMenuItemsLabel,
  StyledSecondaryAction,
  StyledSecondaryLink,
  StyleOutlinedPushPin,
} from './BottomMenuItems.styles';
import { useFindPath } from '../../utilities/useFindPath';
import AddFirmItem from '../AddFirmItem/AddFirmItem';
import BottomMenuItemsSkeleton from '../BottomMenuItemsSkeleton';
import { MenuItem as MenuItemType, SubMenuItem } from '../type';

const BottomMenuItems = ({ setOpenSidebar, open }: BottomMenuItemsProps) => {
  const navigate = useNavigate();
  const [{ firmList, firmId, user, firmInfo }, dispatch] = useStore() as unknown as UseStoreValues;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [pinnedFirms, setPinnedFirms] = useLocalStorageByUser(PINNED_FIRMS_KEY, []);
  const firmFilterInputRef = useRef<HTMLInputElement>(null);
  const firmSelectorMenuListRef = useRef<HTMLUListElement>(null);
  const { setAction } = useContext(UnsavedChangesContext);
  const { features } = useContext(FeaturesContext);

  const path = useFindPath();

  const updatePinStatus = useCallback(
    (id: number, title: string, isPinned: boolean) => {
      let tmpPinnedOptions: any = {};

      if (isPinned) {
        tmpPinnedOptions = pinnedFirms.filter((o: any) => o.value !== id) ?? [];
      } else {
        tmpPinnedOptions = [...pinnedFirms, { value: id, label: title }];
      }

      setPinnedFirms(tmpPinnedOptions);
    },
    [pinnedFirms, setPinnedFirms]
  );

  useEffect(() => {
    if (!open) {
      // If the dialog is false
      if (isNull(anchorEl)) {
        // ...and if firm list it's not open (without anchorEl)
        setFilterSearch('');
      } else {
        // ...and anchorEl exist
        setAnchorEl(null);
      }
    }
  }, [open, anchorEl]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = useSignOutAction({
    // Do not show confirmation dialog if there are pending changes but the user wants to leave.
    onBeforeSignOut: () => setAction(false),
  });

  const handleLogoutClick = (event: React.MouseEvent<HTMLElement>) => {
    // The sidebar card is already interactive, so logout needs to stop bubbling before it triggers parent actions.
    event.preventDefault();
    event.stopPropagation();
    handleSignOut();
  };

  const handleLogoutMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    // Stop the pointer sequence before the surrounding sidebar button can react to this nested control.
    event.stopPropagation();
  };

  const handleLogoutKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleSignOut();
    }
  };

  const createFirmFeatureStatus = useMemo(() => {
    if (features && CREATE_FIRM_ENABLER in features) {
      return features[CREATE_FIRM_ENABLER];
    }
  }, [features]);

  const handleFirmSelection = (firm: StoreFirm) => {
    if (firm?.id && firm.slug) {
      // Navigate to firm summary
      dispatch(companiesAction.setCompanyList(null));
      dispatch(fundsAction.setFundList(null));
      navigate(firmSummaryUrl(firm.slug));
    }

    handleMenuClose();
    setOpenSidebar?.(false);
  };

  const canSeeAuditLogs = useMemo(() => {
    return false; // TODO: Restore audit log
    if (!user?.firms_permissions || !firmInfo) {
      return false;
    }

    const isSuperUser = Boolean(user.is_superuser);
    if (isSuperUser) {
      return true;
    }

    const currentFirmPermission = user.firms_permissions.find(permission => permission.slug === firmInfo.slug);
    if (!currentFirmPermission) {
      return false;
    }

    return VALID_ROLES_FOR_AUDIT_LOGS.has(currentFirmPermission.user_firm_role);
  }, [user, firmInfo]);

  const handleAddFirm = () => {
    handleMenuClose();
    setOpenSidebar?.(false);
    dispatch(globalAction.showFirmStepper(true));
  };

  const getFocusableFirmOptions = useCallback(() => {
    const firmSelectorMenuList = firmSelectorMenuListRef.current;

    if (!firmSelectorMenuList) {
      return [];
    }

    return Array.from(firmSelectorMenuList.querySelectorAll<HTMLElement>('[data-sidebar-firm-option="true"]')).filter(
      option => option.getClientRects().length > 0 && !option.hasAttribute('disabled') && option.getAttribute('aria-disabled') !== 'true'
    );
  }, []);

  const focusFirmOption = useCallback((option: HTMLElement | undefined) => {
    if (!option) {
      return false;
    }

    option.focus();
    option.scrollIntoView({ block: 'nearest' });
    return true;
  }, []);

  const focusFirstFirmOption = useCallback(() => {
    const firmOptions = getFocusableFirmOptions();
    return focusFirmOption(firmOptions[0]);
  }, [focusFirmOption, getFocusableFirmOptions]);

  const focusLastFirmOption = useCallback(() => {
    const firmOptions = getFocusableFirmOptions();
    return focusFirmOption(firmOptions.at(-1));
  }, [focusFirmOption, getFocusableFirmOptions]);

  const focusAdjacentFirmOption = useCallback((currentOption: HTMLElement, direction: 1 | -1) => {
    const firmOptions = getFocusableFirmOptions();
    const currentIndex = firmOptions.indexOf(currentOption);

    if (currentIndex === -1) {
      return false;
    }

    return focusFirmOption(firmOptions[currentIndex + direction]);
  }, [focusFirmOption, getFocusableFirmOptions]);

  const handleFirmFilterKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ARROW_DOWN_KEY && focusFirstFirmOption()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key === ARROW_UP_KEY && focusLastFirmOption()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [focusFirstFirmOption, focusLastFirmOption]);

  const handleFirmOptionsKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    const currentOption = event.currentTarget;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      currentOption.closest<HTMLAnchorElement>('a[href]')?.click();
      return;
    }

    if (event.key === ESCAPE_KEY && firmFilterInputRef.current) {
      event.preventDefault();
      event.stopPropagation();
      firmFilterInputRef.current.focus();
      return;
    }

    if (event.key === ARROW_DOWN_KEY) {
      if (focusAdjacentFirmOption(currentOption, 1)) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (event.key === ARROW_UP_KEY) {
      if (focusAdjacentFirmOption(currentOption, -1)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }, [focusAdjacentFirmOption]);

  const selectedFirm = useMemo<StoreFirm | typeof SELECT_A_FIRM>(() => {
    if (isEmpty(firmList) || !firmId) {
      return SELECT_A_FIRM;
    }
    return (firmList ?? []).find(firm => firm.id === firmId) ?? SELECT_A_FIRM;
  }, [firmList, firmId]);

  const notSelectedFirms = useMemo((): SubMenuItem[] => {
    let tmpList: SubMenuItem[] = [];
    if (!Array.isArray(firmList) || isEmpty(firmList)) {
      tmpList = [];
    } else if (firmList.length && firmId) {
      let filteredFirm: SubMenuItem[] = [];
      // Get the list of firms without the selected one
      filteredFirm = firmList
        .filter(firm => firm.id !== firmId)
        .map(firm => {
          const isPinned = Boolean(pinnedFirms.find((pinnedFirm: any) => pinnedFirm.value === firm.id));

          return {
            id: firm.id,
            title: firm.name,
            slug: firm.slug,
            to: '/#',
            disabled: false,
            isPinnable: true,
            isPinned,
            updatePinStatus: (id: number, title: string, isPinned: boolean) => updatePinStatus(id, title, isPinned),
            callback: () => handleFirmSelection(firm),
          };
        });

      if (filterSearch) {
        filteredFirm = filteredFirm.filter(firm => {
          const firmTitle = firm.title.toLowerCase();
          return firmTitle.includes(filterSearch);
        });
      }
      if (filterSearch && !filteredFirm.length) {
        filteredFirm.push({
          title: 'Not found',
          disabled: true,
        });
      }

      // Sort by pinned status (pinned items first)
      return orderBy(filteredFirm, ['isPinned'], ['desc']);
    }
    if (typeof path === 'string' && path === firmCompGroupsUrl('0')) {
      return [];
    }
    return tmpList;
  }, [firmList, firmId, filterSearch, path, pinnedFirms, updatePinStatus]);

  const items: SubMenuItem[] = [];

  if (firmInfo?.slug) {
    items.push({
      title: 'Account Settings',
      slug: 'account-settings',
      url: () => `${userProfileTabUrl(firmInfo.slug, ACCOUNT_SETTINGS_TAB)}`,
      callback: () => {
        handleMenuClose();
        setOpenSidebar?.(false);
      },
    });
  }

  if (canSeeAuditLogs && firmInfo?.slug) {
    items.push({
      title: 'Audit Logs',
      slug: 'audit-logs',
      url: () => `${userProfileTabUrl(firmInfo.slug, AUDIT_LOGS_TAB)}`,
      callback: () => {
        handleMenuClose();
        setOpenSidebar?.(false);
      },
    });
  }

  if (user?.is_staff) {
    items.push({
      title: 'Switch to Workboard',
      slug: 'switch-app',
      url: getPreviousWorkboardRoute() || WORKBOARD_CURRENT_VALUATIONS_URL,
      callback: () => {
        // Once the user is already authenticated in portfolio, switching back to Workboard should not bounce through the auth shell.
        savePreviousPortfolioRoute(globalThis.window.location.pathname, user?.id);
        clearAppAutoSelect();
        navigate(getPreviousWorkboardRoute() || WORKBOARD_CURRENT_VALUATIONS_URL);
        handleMenuClose();
        setOpenSidebar?.(false);
      },
    });
  }

  if (process.env.REACT_APP_SCALAR_FEEDBACK_API_PROJECT_ID) {
    items.push({
      title: 'Feedback',
      slug: 'feedback',
      callback: () => {
        if (user) {
          ShowFeedbackWidget(user as CustomUser);
        }
        handleMenuClose();
        setOpenSidebar?.(false);
      },
    });
  }

  const userMenuItems = useMemo(() => items, [firmInfo]);

  const menuItems = useMemo((): MenuItemType[] => {
    if (isEmpty(selectedFirm) || isEmpty(user) || (isEmpty(userMenuItems) && firmInfo?.slug)) {
      return [];
    }

    if (!user) {
      return [];
    }

    const selectedFirmInfo = typeof selectedFirm === 'string' ? null : selectedFirm;
    const userDisplayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || user.email || '';

    const userSection: MenuItemType = {
      textPrimary: userDisplayName,
      textSecondary: (
        <StyledSecondaryAction
          role="button"
          tabIndex={0}
          onMouseDown={handleLogoutMouseDown}
          onMouseUp={handleLogoutClick}
          onClick={handleLogoutClick}
          onKeyDown={handleLogoutKeyDown}
        >
          {capitalizeWords('Sign out')}
        </StyledSecondaryAction>
      ),
      slug: USER_MENU_SLUG,
      avatar: (
        <StyledAvatar open={open}>
          <Avatar src={user.profile?.picture_url ?? undefined} />
        </StyledAvatar>
      ),
      subItems: userMenuItems,
    };

    return selectedFirmInfo?.slug
      ? [
        {
          textPrimary: selectedFirmInfo.name ?? SELECT_A_FIRM,
          textSecondary: selectedFirmInfo.slug && (
            <StyledSecondaryLink as={Link} to={firmSummaryUrl(selectedFirmInfo.slug)}>
              {capitalizeWords('View Details')}
            </StyledSecondaryLink>
          ),
          slug: FIRMS_MENU_SLUG,
          avatar: <StyledAvatar open={open}>{getInitials({ name: selectedFirmInfo.name })}</StyledAvatar>,
          subItems: notSelectedFirms,
          subItemsLabel:
            Array.isArray(firmList) && firmList.length > FILTER_FIRM_NUMBER
              ? (
                <FirmFilter
                  inputRef={firmFilterInputRef}
                  onInputKeyDown={handleFirmFilterKeyDown}
                  setFilterSearch={setFilterSearch}
                />
              )
              : SELECT_FIRM,
        },
        userSection,
      ]
      : [userSection];
  }, [
    selectedFirm,
    notSelectedFirms,
    user,
    userMenuItems,
    firmInfo?.slug,
    firmList,
    handleFirmFilterKeyDown,
    handleSignOut,
    open,
  ]);

  const shouldDisplayMoreVert = (menuItem: MenuItemType) =>
    Boolean(user?.is_superuser) ||
    createFirmFeatureStatus ||
    menuItem.slug === USER_MENU_SLUG ||
    (menuItem.subItems && menuItem.subItems.length > 0);

  const getLinkDestination = useCallback(
    (option: SubMenuItem): string => {
      if (!(option.to || option.url)) {
        return '#';
      }

      const urlValue = typeof option.url === 'function' ? option.url() : option.url;

      // || here as empty string is falsy
      return urlValue || firmSummaryUrl(option.slug!);
    },
    [firmSummaryUrl]
  );

  const displayAddFirm = () => {
    if (user?.is_superuser) {
      return <AddFirmItem handleAddFirm={handleAddFirm} onKeyDown={handleFirmOptionsKeyDown} />;
    }
    return (
      <FeatureEnabler enabler={CREATE_FIRM_ENABLER} hideComponent>
        <AddFirmItem handleAddFirm={handleAddFirm} onKeyDown={handleFirmOptionsKeyDown} />
      </FeatureEnabler>
    );
  };

  if (isUndefined(notSelectedFirms) || (isEmpty(userMenuItems) && firmInfo?.slug)) {
    return <BottomMenuItemsSkeleton />;
  }

  return (
    <List style={{ marginBottom: '4rem' }}>
      {menuItems.map((menuItem: MenuItemType) => (
        <div key={menuItem.slug} id={`sidebar-bottom__menu-${menuItem.slug}`}>
          <ListItem>
            <ListItemIcon className="sidebar-bottom__menu-icon">{menuItem.avatar}</ListItemIcon>
            <ListItemText
              primary={menuItem.textPrimary}
              secondary={menuItem.textSecondary}
              slotProps={{
                primary: {
                  sx: {
                    ...OverflowText,
                    color: 'white',
                    fontWeight: 700,
                  },
                },
                secondary: {
                  component: 'div',
                  sx: OverflowText,
                },
              }}
              className="sidebar-bottom__menu-text"
            />
            {open && shouldDisplayMoreVert(menuItem) && (
              <IconButton
                id={`${menuItem.slug}-button`}
                aria-label="more"
                aria-controls={menuItem.slug}
                aria-haspopup="true"
                component="span"
                onClick={handleMenuClick}
                className="sidebar-bottom__menu-button"
              >
                <StyledBottomMenuSubMenuIcon fontSize="small" />
              </IconButton>
            )}
          </ListItem>
          {((menuItem?.subItems && menuItem?.subItems?.length > 0) || createFirmFeatureStatus) && (
            <Menu
              id={`sidebar-bottom__dropdown-${menuItem.slug}`}
              anchorEl={anchorEl}
              open={open && anchorEl ? anchorEl.getAttribute('aria-controls') === menuItem.slug : false}
              onClose={handleMenuClose}
              MenuListProps={menuItem.slug === FIRMS_MENU_SLUG ? {
                ref: firmSelectorMenuListRef,
              } : undefined}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: ITEM_HEIGHT,
                  },
                  square: true,
                },
              }}>
              {menuItem.subItemsLabel && menuItem.subItems && menuItem.subItems.length > 0 && (
                <StyledMenuItemsLabel>{menuItem.subItemsLabel}</StyledMenuItemsLabel>
              )}
              {menuItem.subItems?.map((option: SubMenuItem) => (
                <div key={option.slug}>
                  {/* Take into account individual firms */}
                  {option.disabled ? (
                    <StyledMenuItem disabled>{option.title}</StyledMenuItem>
                  ) : (
                    <StyledLink to={getLinkDestination(option)}>
                      <ListItem
                        component="button"
                        className={`sidebar-bottom__dropdown-item sidebar-bottom__dropdown-item-${option.slug}`}
                        data-sidebar-firm-option={menuItem.slug === FIRMS_MENU_SLUG ? 'true' : undefined}
                        onKeyDown={menuItem.slug === FIRMS_MENU_SLUG ? handleFirmOptionsKeyDown : undefined}
                        sx={{ width: '100%' }}
                        onClick={option.callback}>
                        {option.isPinnable && (
                          <Tooltip title={option.isPinned ? 'Unpin' : 'Pin'}>
                            <IconButton
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (option.updatePinStatus && option.id) {
                                  option.updatePinStatus(option.id, option.title, option.isPinned || false);
                                }
                              }}
                              size="small"
                              aria-label="pin"
                              style={{ marginRight: '8px' }}>
                              {option.isPinned ? (
                                <PushPin fontSize="small" htmlColor="rgba(0, 0, 0, 0.6)" />
                              ) : (
                                <StyleOutlinedPushPin fontSize="small" htmlColor="rgba(0, 0, 0, 0.6)" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <ListItemText primary={option.title} />
                      </ListItem>
                    </StyledLink>
                  )}
                </div>
              ))}
              {menuItem.slug === FIRMS_MENU_SLUG && displayAddFirm()}
            </Menu>
          )}
        </div>
      ))}
    </List>
  );
};

export default BottomMenuItems;
